import { OnDestroy, ApplicationRef, Injectable } from '@angular/core';
import {
  SwUpdate,
  VersionEvent,
  UnrecoverableStateEvent
} from '@angular/service-worker';
import { BehaviorSubject, Observable, Subscription, interval, of } from 'rxjs';
import { first, switchMap } from 'rxjs/operators';

import { MonitoringService } from '@core/services/monitoring.service';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root'
})
export class AppStatusService implements OnDestroy {
  private subscriptions: Subscription[] = [];
  private newVersionSubject = new BehaviorSubject<string>(
    undefined as unknown as string
  );
  private updateCheckIntervalMins: number = 60;
  private updateAvailable: boolean = false;
  public currentVersion: string | null = null;

  constructor(
    private appRef: ApplicationRef,
    private swUpdater: SwUpdate,
    private monitoringService: MonitoringService
  ) {
    this.updateCheckIntervalMins = environment.serviceWorkersCheckUpdatesMins;
    this.initialiseUpdater();
  }

  ngOnDestroy(): void {
    this.logToConsoleDebug('[ App ] Destroy...');
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  get isServiceWorkerEnabled(): boolean {
    return this.swUpdater.isEnabled;
  }

  get hasAvailableUpdate(): boolean {
    return this.updateAvailable;
  }

  get onlineStatus() {
    return navigator.onLine;
  }

  async initialiseUpdater() {
    this.logToConsoleDebug(`[ App ] Online: ${this.onlineStatus}`);
    this.logToConsoleDebug(
      `[ App ] Service workers enabled: ${this.isServiceWorkerEnabled}`
    );

    if (this.isServiceWorkerEnabled) {
      await this.checkForNewVersionAndReloadIfAvailable();
      this.logToConsoleDebug(
        `[ App ] Check for updates every: ${this.updateCheckIntervalMins} mins`
      );

      // Subscribes to events
      this.subscriptions.push(
        this.swUpdater.versionUpdates.subscribe((e: any) =>
          this.onNewVersionEvent(e)
        )
      );
      this.subscriptions.push(
        this.swUpdater.unrecoverable.subscribe((e: any) =>
          this.onUnrecoverableState(e)
        )
      );

      // Allow the app to stabilize first, before starting polling for updates
      this.subscriptions.push(
        this.appRef.isStable
          .pipe(
            first((isStable) => isStable === true),
            switchMap(() => interval(1000 * 60 * this.updateCheckIntervalMins))
          )
          .subscribe(() => this.checkForUpdate())
      );
    }
  }

  public async checkForNewVersionAndReloadIfAvailable() {
    this.logToConsoleDebug('[ App ] Checking for updates...');
    const newVersionAvailable = await this.swUpdater.checkForUpdate();
    this.logToConsoleDebug(
      `[ App ] New version available: ${newVersionAvailable}`
    );
    if (newVersionAvailable) {
      window.location.reload();
      this.updateAvailable = newVersionAvailable;
    }
  }

  private async checkForUpdate() {
    if (this.isServiceWorkerEnabled) {
      this.logToConsoleDebug('[ App ] Checking for updates...');
      let newVersionAvailable = await this.swUpdater.checkForUpdate();
      this.logToConsoleDebug(
        `[ App ] New version available: ${newVersionAvailable}`
      );
      this.updateAvailable = newVersionAvailable;
    }
  }

  private async onNewVersionEvent(event: VersionEvent) {
    switch (event.type) {
      case 'VERSION_DETECTED':
        this.logToConsoleDebug(
          `[ App ] Downloading new app version: ${event.version.hash}`
        );
        break;

      case 'VERSION_READY':
        this.currentVersion = event.currentVersion.appData
          ? (event.currentVersion.appData as any).newVersion
          : null;
        this.logToConsoleDebug(
          `[ App ] Current version: ${event.currentVersion.hash}`
        );
        this.logToConsoleDebug(
          `[ App ] New version ready: ${event.latestVersion.hash}`
        );
        if (event.latestVersion.appData) {
          const appData = event.latestVersion.appData as any;
          this.logToConsoleDebug(
            `[ App ] New version number: ${appData.newVersion}`
          );
          this.alertNewVersionAvailable(appData.newVersion);
        }
        break;

      case 'VERSION_INSTALLATION_FAILED':
        this.logToConsoleError(
          `[ App ] Failed to install version '${event.version.hash}': ${event.error}`,
          event
        );
        break;
    }
  }

  private async onUnrecoverableState(event: UnrecoverableStateEvent) {
    this.logToConsoleDebug(`[ App ] Unrecoverable state: ${event.reason}`);
  }

  public detectNewVersion(): Observable<string> {
    return this.newVersionSubject.asObservable();
  }

  private alertNewVersionAvailable(version: string) {
    this.newVersionSubject.next(version);
  }

  private logToConsoleDebug(message: string): void {
    if (environment.enableServiceWorkerVerboseLogging) {
      this.monitoringService.logTrace(message);
    }
  }

  private logToConsoleError(message: string, event: any): void {
    console.error(message, event);
    this.monitoringService.logException(new Error(message), event);
  }
}

@Injectable()
export class AppStatusServiceStub {
  isServiceWorkerEnabled() {}
  hasAvailableUpdate() {}
  detectNewVersion(): Observable<string> {
    return of('');
  }
  async checkForUpdate() {}
  async onNewVersionEvent() {}
  async onUnrecoverableState() {}
}
